import React, { useMemo, useState, useEffect } from 'react'
import { format } from 'date-fns'
import { Calendar, Clock, Eye, EyeOff, KeyRound, Mail, Shield, UserRound } from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import { formatRoleLabel } from '../lib/roleLabel'
import { getDisplayName } from '../lib/profileDisplayName'
import { supabase } from '../lib/supabase'

const MIN_PASSWORD_LEN = 8

const cardBase = {
  backgroundColor: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--color-border)',
  borderTop: '3px solid var(--color-accent)',
  boxShadow: 'var(--shadow-sm)',
  padding: '24px 26px',
}

const labelStyle = {
  fontSize: '11px',
  fontWeight: '700',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '6px',
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1.5px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-body)',
  color: 'var(--color-text-primary)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
}

function hasEmailPasswordIdentity(user) {
  return Boolean(user?.identities?.some((i) => i.provider === 'email'))
}

function PasswordField({ id, label, value, onChange, autoComplete, show, onToggleShow, disabled, invalid }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={id} style={{ ...labelStyle, display: 'block', marginBottom: '8px' }}>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            ...inputStyle,
            paddingRight: '44px',
            borderColor: invalid ? '#dc2626' : 'var(--color-border)',
          }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          disabled={disabled}
          aria-label={show ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute',
            right: '8px',
            background: 'none',
            border: 'none',
            padding: '6px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: 'var(--color-text-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const { user, profile, refreshProfile } = useAuth()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState('')
  const [nameSuccess, setNameSuccess] = useState('')

  useEffect(() => {
    setFirstName(profile?.first_name != null ? String(profile.first_name) : '')
    setLastName(profile?.last_name != null ? String(profile.last_name) : '')
  }, [profile?.id, profile?.first_name, profile?.last_name])

  const namePreview = useMemo(() => {
    const p = { first_name: firstName, last_name: lastName }
    return getDisplayName(p)
  }, [firstName, lastName])

  const handleSaveNames = async (e) => {
    e.preventDefault()
    if (!user?.id) return
    setNameError('')
    setNameSuccess('')
    setNameSaving(true)
    try {
      const fn = firstName.trim()
      const ln = lastName.trim()
      const { error } = await supabase
        .from('profiles')
        .update({ first_name: fn || null, last_name: ln || null })
        .eq('id', user.id)
      if (error) throw error
      await refreshProfile()
      setNameSuccess('Display name saved.')
    } catch (err) {
      setNameError(err?.message || 'Could not save display name.')
    } finally {
      setNameSaving(false)
    }
  }

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')

  const canChangePassword = useMemo(() => hasEmailPasswordIdentity(user), [user])

  const lastSignInLabel = useMemo(() => {
    const raw = user?.last_sign_in_at
    if (!raw) return '—'
    try {
      return format(new Date(raw), "MMMM d, yyyy 'at' h:mm a")
    } catch {
      return '—'
    }
  }, [user?.last_sign_in_at])

  const memberSinceLabel = useMemo(() => {
    if (!profile?.created_at) return '—'
    try {
      return format(new Date(profile.created_at), 'MMMM d, yyyy')
    } catch {
      return '—'
    }
  }, [profile?.created_at])

  const validateClient = () => {
    const next = {}
    if (!currentPassword) next.current = 'Current password is required.'
    if (!newPassword) next.new = 'New password is required.'
    else if (newPassword.length < MIN_PASSWORD_LEN)
      next.new = `Use at least ${MIN_PASSWORD_LEN} characters.`
    if (!confirmPassword) next.confirm = 'Confirm your new password.'
    else if (newPassword !== confirmPassword) next.confirm = 'New passwords do not match.'
    if (currentPassword && newPassword && currentPassword === newPassword)
      next.new = 'New password must be different from your current password.'
    setFieldErrors(next)
    return Object.keys(next).length === 0
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    setSuccessMessage('')
    if (!validateClient()) return

    const email = user?.email ?? profile?.email
    if (!email) {
      setFormError('Could not determine your email. Try signing out and back in.')
      return
    }

    setSubmitting(true)
    try {
      const { error: signErr } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      })
      if (signErr) {
        setFormError('Current password is incorrect.')
        setCurrentPassword('')
        setSubmitting(false)
        return
      }

      const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword })
      if (updateErr) {
        setFormError(updateErr.message || 'Could not update password. Try again.')
        setSubmitting(false)
        return
      }

      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setFieldErrors({})
      setSuccessMessage('Password updated. You remain signed in on this device.')
    } catch (err) {
      setFormError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '28px 32px' }}>
      <div
        style={{
          maxWidth: '960px',
          margin: '0 auto',
        }}
      >
        <div style={{ marginBottom: '28px' }}>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 6px 0',
              color: 'var(--color-text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Profile
          </h1>
          <p style={{ color: 'var(--color-text-muted)', margin: 0, fontSize: '15px', lineHeight: 1.5 }}>
            Your account details and security settings for SMG Client Management.
          </p>
        </div>

        <div
          aria-live="polite"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
            gap: '24px',
            alignItems: 'start',
          }}
        >
          {/* Account */}
          <section style={cardBase}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(250, 204, 21, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-primary)',
                }}
              >
                <Shield size={20} strokeWidth={2} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                  Account
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  Display name, read-only organization fields
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <form onSubmit={handleSaveNames}>
                <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <UserRound size={12} strokeWidth={2.5} aria-hidden />
                  Display name
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '12px',
                    marginBottom: '12px',
                  }}
                >
                  <div>
                    <label htmlFor="profile-first-name" style={{ ...labelStyle, textTransform: 'none', fontWeight: '600' }}>
                      First name
                    </label>
                    <input
                      id="profile-first-name"
                      type="text"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={nameSaving}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="profile-last-name" style={{ ...labelStyle, textTransform: 'none', fontWeight: '600' }}>
                      Last name
                    </label>
                    <input
                      id="profile-last-name"
                      type="text"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={nameSaving}
                      style={inputStyle}
                    />
                  </div>
                </div>
                {namePreview ? (
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    Preview: <strong style={{ color: 'var(--color-text-primary)' }}>{namePreview}</strong>
                  </p>
                ) : null}
                {nameError ? (
                  <p role="alert" style={{ margin: '0 0 10px', fontSize: '13px', color: '#b91c1c' }}>
                    {nameError}
                  </p>
                ) : null}
                {nameSuccess ? (
                  <p role="status" style={{ margin: '0 0 10px', fontSize: '13px', color: '#166534' }}>
                    {nameSuccess}
                  </p>
                ) : null}
                <button
                  type="submit"
                  disabled={nameSaving}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: nameSaving ? 'var(--color-border)' : 'var(--color-accent)',
                    color: '#0f172a',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: nameSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {nameSaving ? 'Saving…' : 'Save display name'}
                </button>
              </form>

              <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '4px 0' }} aria-hidden />

              <div>
                <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Mail size={12} strokeWidth={2.5} aria-hidden />
                  Email
                </div>
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    wordBreak: 'break-all',
                    lineHeight: 1.45,
                  }}
                >
                  {profile?.email ?? user?.email ?? '—'}
                </div>
              </div>

              <div>
                <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Shield size={12} strokeWidth={2.5} aria-hidden />
                  Role
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: '2px',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: 'rgba(250, 204, 21, 0.18)',
                    border: '1px solid rgba(250, 204, 21, 0.45)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {formatRoleLabel(profile?.role)}
                </span>
              </div>

              <div>
                <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={12} strokeWidth={2.5} aria-hidden />
                  Member since
                </div>
                <div style={{ fontSize: '15px', color: 'var(--color-text-primary)' }}>{memberSinceLabel}</div>
              </div>

              <div>
                <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={12} strokeWidth={2.5} aria-hidden />
                  Last sign-in
                </div>
                <div style={{ fontSize: '15px', color: 'var(--color-text-primary)' }}>{lastSignInLabel}</div>
              </div>
            </div>
          </section>

          {/* Security */}
          <section style={cardBase}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(59, 130, 246, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-primary)',
                }}
              >
                <KeyRound size={20} strokeWidth={2} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                  Security
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  Password must be verified before it can be changed
                </p>
              </div>
            </div>

            {!canChangePassword ? (
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: 1.55,
                  color: 'var(--color-text-muted)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-bg-body)',
                  border: '1px solid var(--color-border)',
                }}
              >
                Password changes are not available for this sign-in method. If you use a corporate or social login,
                manage your password through that provider.
              </p>
            ) : (
              <form onSubmit={handlePasswordSubmit} noValidate>
                <PasswordField
                  id="profile-current-password"
                  label="Current password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  autoComplete="current-password"
                  show={showCurrent}
                  onToggleShow={() => setShowCurrent((v) => !v)}
                  disabled={submitting}
                  invalid={Boolean(fieldErrors.current)}
                />
                {fieldErrors.current ? (
                  <p style={{ margin: '-8px 0 12px', fontSize: '13px', color: '#dc2626' }}>{fieldErrors.current}</p>
                ) : null}

                <PasswordField
                  id="profile-new-password"
                  label="New password"
                  value={newPassword}
                  onChange={setNewPassword}
                  autoComplete="new-password"
                  show={showNew}
                  onToggleShow={() => setShowNew((v) => !v)}
                  disabled={submitting}
                  invalid={Boolean(fieldErrors.new)}
                />
                {fieldErrors.new ? (
                  <p style={{ margin: '-8px 0 12px', fontSize: '13px', color: '#dc2626' }}>{fieldErrors.new}</p>
                ) : null}

                <PasswordField
                  id="profile-confirm-password"
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  autoComplete="new-password"
                  show={showConfirm}
                  onToggleShow={() => setShowConfirm((v) => !v)}
                  disabled={submitting}
                  invalid={Boolean(fieldErrors.confirm)}
                />
                {fieldErrors.confirm ? (
                  <p style={{ margin: '-8px 0 12px', fontSize: '13px', color: '#dc2626' }}>{fieldErrors.confirm}</p>
                ) : null}

                {formError ? (
                  <p
                    role="alert"
                    style={{
                      margin: '0 0 14px',
                      fontSize: '14px',
                      color: '#b91c1c',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(254, 226, 226, 0.6)',
                      border: '1px solid rgba(248, 113, 113, 0.5)',
                    }}
                  >
                    {formError}
                  </p>
                ) : null}

                {successMessage ? (
                  <p
                    role="status"
                    style={{
                      margin: '0 0 14px',
                      fontSize: '14px',
                      color: '#166534',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(220, 252, 231, 0.7)',
                      border: '1px solid rgba(74, 222, 128, 0.5)',
                    }}
                  >
                    {successMessage}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    marginTop: '4px',
                    width: '100%',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: submitting ? 'var(--color-border)' : 'var(--color-accent)',
                    color: '#0f172a',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: submitting ? 'none' : '0 2px 8px rgba(250, 204, 21, 0.35)',
                  }}
                >
                  {submitting ? 'Updating…' : 'Update password'}
                </button>
                <p style={{ margin: '12px 0 0', fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                  We verify your current password before applying a new one. Use at least {MIN_PASSWORD_LEN}{' '}
                  characters.
                </p>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
